import { createStyles, FormControl, Grid, makeStyles, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { Button, Dropdown, MdSpace, MoreOptionsMenu, SmSpace, TextField } from 'm2m.components';
import {
  InstrumentoFinanceiro,
  Perfil,
  InstrumentoEdicaoResponse,
  InstrumentoEdicaoStatus,
} from 'common/genericTypes';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from 'store';
import {
  DataGridPro,
  getGridStringOperators,
  GridCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { localizedTextsMap, componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { Cancel, ArrowForward, WatchLater } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { addAlert } from 'store/modules/snackBar';
import { getInstrumentoFinanceiroLongDescription } from 'common/genericDescriptions';

const useStylesTexto = makeStyles(() =>
  createStyles({
    wrapTxt: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  })
);

const instrumentoEdicaoStatusDescriptions: { [key in InstrumentoEdicaoStatus]: string } = {
  [InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO]: 'Aguardando Aprovação',
  [InstrumentoEdicaoStatus.APROVADO]: 'Aprovado',
  [InstrumentoEdicaoStatus.REPROVADO]: 'Reprovado',
};

const SolicitacoesInstrumentosEdicao: React.FC = () => {
  const style = useStylesTexto();
  const history = useHistory();
  const dispatch = useDispatch();
  const getInstrumentosEdicaoApi = useSelector((state: RootState) => state.instrumentoEdicaoApi.getInstrumentosEdicao);
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const getCraByInstrumentoId = useSelector((state: RootState) => state.craApi.getCraByInstrumentoId);
  const getCdcaByInstrumentoId = useSelector((state: RootState) => state.cdcaApi.getCdcaByInstrumentoId);
  const getCotasDeFundoByInstrumentoId = useSelector((state: RootState) => state.cotasDeFundoApi.getCotasDeFundoByInstrumentoId);
  const getCriByInstrumentoId = useSelector((state: RootState) => state.criApi.getCriByInstrumentoId);
  const getDebentureByInstrumentoId = useSelector((state: RootState) => state.debentureApi.getDebentureByInstrumentoId);
  const getNotaComercialByInstrumentoId = useSelector(
    (state: RootState) => state.notaComercialApi.getNotaComercialByInstrumentoId
  );
  const getCertificadoRecebiveisByInstrumentoId = useSelector(
    (state: RootState) => state.certificadoRecebiveisApi.getCertificadoRecebiveisByInstrumentoId
  );
  const [checkedId, setCheckedId] = useState<string>();
  const [edicoes, setEdicoes] = useState<Array<InstrumentoEdicaoResponse>>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState<number>(20);

  const [filterDataFrom, setFilterDataFrom] = useState('');
  const [filterDataTo, setFilterDataTo] = useState('');
  const [filterCodigoIF, setFilterCodigoIF] = useState('');
  const [filterStatus, setFilterStatus] = useState<InstrumentoEdicaoStatus | 'todas'>(
    InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO
  );


  const getEdicoes = useCallback(async (filter = { status: InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO }) => {
    try {
      setLoading(true);
      // A API de edição já deve filtrar pelo usuário logado no backend para a tela de solicitações
      const result = await getInstrumentosEdicaoApi(dispatch, filter);
      setEdicoes(result);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilterDataFrom('');
    setFilterDataTo('');
    setFilterCodigoIF('');
    setFilterStatus(InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO);
    getEdicoes();
  }, [getEdicoes]);

  const handleSearch = useCallback(() => {
    const filters = {
      instrumentoFinanceiroCodigo: filterCodigoIF || undefined,
      status: filterStatus === 'todas' ? undefined : filterStatus,
      dataAlteracaoDe: filterDataFrom || undefined,
      dataAlteracaoAte: filterDataTo || undefined,
    };
    getEdicoes(filters);
  }, [filterDataFrom, filterDataTo, filterCodigoIF, filterStatus, getEdicoes]);

  const handleCancel = async (edicao: InstrumentoEdicaoResponse) => {
    try {
      setLoading(true);
      // await postCancelarEdicaoInstrumento(dispatch, edicao.instrumentoFinanceiroId, edicao.id);
      dispatch(addAlert({ message: `Solicitação de edição para o Instrumento Financeiro ${edicao.instrumentoFinanceiroCodigo} cancelada com sucesso.`, severity: 'success' }));
      await getEdicoes();
    } catch (error: any) {
      dispatch(addAlert({ message: error.message || 'Erro ao cancelar a solicitação', severity: 'error' }));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalhes = async (edicao: InstrumentoEdicaoResponse) => {
    try {
      setLoading(true);
      switch (edicao.tipo) {
        case InstrumentoFinanceiro.Cra:
          const cra = await getCraByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/cras/${cra.craId}`);
          break;
        case InstrumentoFinanceiro.Cdca:
          const cdca = await getCdcaByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/cdcas/${cdca.cdcaId}`);
          break;
        case InstrumentoFinanceiro.CotasDeFundo:
          const cotasDeFundo = await getCotasDeFundoByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/cotas-de-fundos/${cotasDeFundo.cotasDeFundoId}`);
          break;
        case InstrumentoFinanceiro.Cri:
          const cri = await getCriByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/cris/${cri.criId}`);
          break;
        case InstrumentoFinanceiro.Debenture:
          const debenture = await getDebentureByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/debentures/${debenture.debentureId}`);
          break;
        case InstrumentoFinanceiro.NotaComercial:
          const nc = await getNotaComercialByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/notas-comerciais/${nc.notaComercialId}`);
          break;
        case InstrumentoFinanceiro.CertificadoRecebiveis:
          const cr = await getCertificadoRecebiveisByInstrumentoId(dispatch, edicao.instrumentoFinanceiroId, true);
          history.push(`/instrumentos/gerenciar/certificados-recebiveis/${cr.certificadoRecebiveisId}`);
          break;
      }
    } catch (error) {
      console.error('Error fetching instrument details:', error);
      dispatch(addAlert({ message: 'Erro ao buscar detalhes do instrumento', severity: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getEdicoes();
  }, [getEdicoes]);

  const classes = useGenericStyles();

  const getStatusIcon = useCallback(
    (status: InstrumentoEdicaoStatus) => {
      switch (status) {
        case InstrumentoEdicaoStatus.APROVADO:
          return <CheckCircleIcon fontSize="small" className={classes.successIcon} />;
        case InstrumentoEdicaoStatus.REPROVADO:
          return <Cancel fontSize="small" className={classes.errorIcon} />;
        case InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO:
          return <WatchLater fontSize="small" className={classes.waitingIcon} />;
      }
    },
    [classes]
  );

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'string',
        filterable: false,
        sortable: false,
        width:70,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoEdicaoResponse).status,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <>
              {getStatusIcon(edicao.status) && (
                <Tooltip
                  title={instrumentoEdicaoStatusDescriptions[edicao.status]}
                  aria-label={instrumentoEdicaoStatusDescriptions[edicao.status]}
                >
                  {getStatusIcon(edicao.status)}
                </Tooltip>
              )}
            </>
          );
        },
      },
      {
        field: 'CODIGO',
        headerName: 'Código',
        type: 'string',
        filterable: false,
        sortable: false,
        flex: 0.8,
        width: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoEdicaoResponse).instrumentoFinanceiroCodigo,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
              {edicao.instrumentoFinanceiroCodigo}
            </Typography>
          );
        },
      },
      {
        field: 'TIPO',
        headerName: 'Tipo',
        type: 'string',
        flex: 1,
        valueOptions: ["CDCA", "Cotas de Fundo", "CRA", "CRI", "Certificado de Recebíveis", "Debênture", "Nota Comercial"],
        filterable: false,
        sortable: false,
        width: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoEdicaoResponse).tipo,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
              {getInstrumentoFinanceiroLongDescription[edicao.tipo.toString()]}
            </Typography>
          );
        },
      },
      {
        field: 'DATA_ALTERACAO',
        headerName: 'Data Alteração',
        type: 'date',
        filterable: false,
        sortable: false,
        flex: 0.5,
        width: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoEdicaoResponse).dataAlteracao,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
              {new Date(edicao.dataAlteracao).toLocaleDateString('pt-BR')}
            </Typography>
          );
        },
      },
      {
        field: 'DATA_CRIACAO',
        headerName: 'Data Criação',
        type: 'date',
        filterable: false,
        sortable: false,
        flex: 0.5,
        width: 150, 
        valueGetter: (params: GridValueGetterParams) => (params.row as InstrumentoEdicaoResponse).dataCriacao,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <Typography className={style.wrapTxt} variant="body2">
              {new Date(edicao.dataCriacao).toLocaleDateString('pt-BR')}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Ações',
        filterable: false,
        sortable: false,
        disableExport: true,
        width: 80,
        minWidth: 80,
        renderCell: (params: GridCellParams) => {
          const edicao = params.row as InstrumentoEdicaoResponse;
          return (
            <MoreOptionsMenu>
              {!isUsuarioLeitura && edicao.status === InstrumentoEdicaoStatus.AGUARDANDO_APROVACAO && (
                <MenuItem 
                  disabled={Boolean(loading)}
                  onClick={() => handleCancel(edicao)}
                >
                  <Cancel fontSize="small" style={{ marginRight: '10px' }} />
                  {'Cancelar'}
                </MenuItem>
              )}
              <MenuItem 
                onClick={() => handleVerDetalhes(edicao)}
                disabled={Boolean(loading)}
              >
                <ArrowForward fontSize="small" style={{ marginRight: '10px' }} />
                {'Mais Detalhes'}
              </MenuItem>
            </MoreOptionsMenu>
          );
        },
      },
    ],
    [checkedId, loading, edicoes]
  );

  return (
    <>
      <MdSpace />
      <Grid container alignItems="center">
        <Grid container spacing={1} alignItems="center">
          <Grid item>
            <Typography>{'Edição DE'}</Typography>
            <FormControl fullWidth>
              <TextField
                type="date"
                value={filterDataFrom}
                onChange={(value) => {
                  setFilterDataFrom(value);
                  
                  if (filterDataTo && filterDataTo < value) {
                    setFilterDataTo(value);
                  }
                }}
              />
            </FormControl>
          </Grid>
          <Grid item>
            <Typography>{'Edição ATÉ'}</Typography>
            <FormControl fullWidth>
              <TextField
                type="date"
                value={filterDataTo}
                onChange={(value) => setFilterDataTo(value)}
                inputProps={{ min: filterDataFrom || undefined }}
              />
            </FormControl>
          </Grid>
          <Grid item>
            <Typography>{'Código IF'}</Typography>
            <TextField
              value={filterCodigoIF}
              onChange={(value) => setFilterCodigoIF(value)}
              placeholder="Digite o código IF"
            />
          </Grid>
          <Grid item style={{ minWidth: '200px'}}>
            <Typography>{'Status'}</Typography>
            <Dropdown
              options={[
                { description: 'Todos', value: 'todas' },
                ...Object.values(InstrumentoEdicaoStatus).map(status => ({
                  description: instrumentoEdicaoStatusDescriptions[status],
                  value: status,
                })),
              ]}
              value={filterStatus}
              onChange={(value) => setFilterStatus(value as InstrumentoEdicaoStatus | 'todas')}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              type="button"
              style={{ marginRight: '8px' }}
            >
              {'Pesquisar'}
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              type="button"
            >
              {'Limpar Filtros'}
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <SmSpace />

      <DataGridPro
        autoHeight
        rows={edicoes}
        columns={getGridColumns()}
        loading={loading}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
        rowsPerPageOptions={rowsPerPageOptionsValues}
        components={{ Toolbar: CustomToolBarUtils }}
        componentsProps={componentsPropsValues}
        localeText={localizedTextsMap}
      />
    </>
  );
};

export default SolicitacoesInstrumentosEdicao;
